import videojs from 'video.js';

import { getFullscreenToggleIndex, isPresentationId } from '../../util';
import { Path } from '../../api/config.js';

import OpenInNewIcon from '../../assets/images/open_in_new-white-24dp.svg';

const _menuButton = new WeakMap();
const _popup = new WeakMap();
const _message = new WeakMap();
let localize;

/**
 * Returns a reference to the Popout module.
 * @function popout
 * @returns {PopoutPlugin}
 * @memberof MediasitePlayer#
 */

/**
 * Configures the Popout feature.<br><br>
 * Note: Popout functionality can only be enabled if the current player instance is running at a URL other than the default player page, or is currently embedded in an <code>&lt;iframe&gt;</code>. Setting {@link PopoutPlugin#enabled} will have no effect unless at least one of these conditions is true.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class PopoutPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        localize = player.localize?.bind(player);
        super(player, options);

        // Add a MenuButton to the ControlBar
        const MenuButton = videojs.getComponent('MenuButton');
        const button = new MenuButton(player, { name: 'PopoutMenuButton' });
        button.addClass('mediasite-player__custom-controlbar-button');
        button.addClass('vjs-popout-button');
        button.controlText(localize('Pop Out'));
        button.el().style.setProperty('--custom-controlbar-icon', `url("${OpenInNewIcon}")`);

        this.on(button, ['click', 'touchstart'], this.open);

        _menuButton.set(this, button);

        player.ready(() => {
            player.getChild('ControlBar').addChild(button, null, getFullscreenToggleIndex(player) - 1);
            player.collapsedButtons?.push({
                pluginName: this.name,
                label: localize('Pop Out'),
                buttonName: 'PopoutMenuButton',
                class: 'vjs-popout-button',
                iconUrl: OpenInNewIcon
            });
        });
    }

    /**
     * A boolean indicating whether or not the Popout feature is enabled.
     * @type {boolean}
     * @example
     * player.popout.enabled = true;
     */
    get enabled() {
        return !_menuButton.get(this).hasClass('vjs-hidden');
    }

    set enabled(enabled) {
        // Only allow enabling the popout feature if we're embedded or not running on the main player page.
        const menuButton = _menuButton.get(this);
        const inIFrame = window.parent !== window;
        const onPlayerPage = location.pathname.startsWith(Path.ApplicationRoot);
        if (enabled && (inIFrame || !onPlayerPage)) {
            menuButton.removeClass('vjs-hidden');
        }
        else {
            menuButton.addClass('vjs-hidden');
            _popup.get(this)?.close?.();
        }
    }

    /**
     * Pauses playback and opens the presentation in a new window on the main player page.
     * @example
     * player.popout.open();
     */
    open() {
        this.player.pause();

        // Note: we can only do this if we know the presentation ID.
        const id = this.player.loader()?.presentationId;
        if (isPresentationId(id)) {
            const playFrom = Math.floor(this.player.currentTime() * 1000); // playFrom param expects ms
            // If embedded in channel we pass collection id for channel registration authentication
            const channelId = window?.SiteData?.CollectionId;
            const playLink = channelId ? `${Path.ApplicationRoot}/${id}?playFrom=${playFrom}&popout=true&Collection=${channelId}` : `${Path.ApplicationRoot}/${id}?playFrom=${playFrom}&popout=true`;
            const windowName = this.player.loader()?.presentationId ?? 'playerpreview';
            const windowOptions = `width=800,height=600,scrollbars=yes,resizable=yes`;

            const popup = window.open(playLink, windowName, windowOptions);

            popup.focus();
            popup.addEventListener('unload', () => this.#handlePopupClose());
            _popup.set(this, popup);

            const popoutModalMessage = this.player.showMessage(localize('Opened presentation in a new window.'));
            _message.set(this, popoutModalMessage);
        }
    }

    /**
     * Closes the popout window if it is currently open.
     * @example
     * player.popout.close();
     */
    close() {
        _popup.get(this)?.close?.();
    }

    /**
     * A boolean indicating whether or not the popout window is currently open.<br><br>Note: this does NOT indicate if this player instance itself is displaying as a popout; rather, it indicates whether or not a child window (the "popout" player) is present.
     * @type {boolean}
     * @example
     * const isPopoutOpen = player.popout.isOpen();
     */
    get isOpen() {
        const popup = _popup.get(this);
        return !(!popup || (popup?.closed));
    }

    #handlePopupClose() {
        const popup = _popup.get(this);
        setTimeout(() => {
            if (popup.closed) {
                _popup.set(this, null);

                const popoutModalMessage = _message.get(this);
                if (popoutModalMessage) {
                    popoutModalMessage.close();
                    _message.set(this);
                }
            }
        }, 0);
    }

    dispose() {
        const menuButton = _menuButton.get(this);

        menuButton?.dispose();
        this.player.getChild('ControlBar')?.removeChild(menuButton);

        if (this.isOpen) {
            this.close();
        }

        super.dispose();
    }
}

export default PopoutPlugin;
